import React, { useEffect, useState } from 'react';
import { Alert, Button, Empty, Progress, Spin, Tag } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { correctionAPI, essayAPI } from '../../services/api';
import { CorrectionDetail, EssayTypes } from '../../types';
import { generateComments } from '../../utils/generateComments';
import { getTopicFromEssay } from '../../utils/topicHelper';

const H5ResultPage: React.FC = () => {
  const { essayId } = useParams<{ essayId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CorrectionDetail | null>(null);
  const [fallbackMessage, setFallbackMessage] = useState('');

  useEffect(() => {
    const fetchResult = async () => {
      if (!essayId) return;
      setLoading(true);
      try {
        const response = await correctionAPI.get(parseInt(essayId, 10), { skipErrorHandler: true });
        setData(response.data);
        setFallbackMessage('');
      } catch (error: any) {
        try {
          const essayResponse = await essayAPI.get(parseInt(essayId, 10));
          const essay = essayResponse.data.essay;
          setFallbackMessage(
            essay.status === 'failed'
              ? '作文处理失败，请回到记录中重新处理'
              : '批改结果还未生成，请稍后刷新'
          );
        } catch {
          setFallbackMessage('未找到这篇作文');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [essayId]);

  if (loading) {
    return (
      <div className="h5-page">
        <section className="h5-panel h5-loading-panel">
          <Spin tip="加载报告中..." />
        </section>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="h5-page">
        <section className="h5-panel">
          <Empty description={fallbackMessage || '暂无批改报告'} />
          <Button block type="primary" onClick={() => navigate('/h5/history')}>
            返回记录
          </Button>
        </section>
      </div>
    );
  }

  const { correction, errors = [], essay, dimension_scores } = data;
  const topic = getTopicFromEssay(essay);
  const isApplication = topic?.essay_type === EssayTypes.APPLICATION;
  const maxTotal = isApplication ? 15 : 25;
  const percent = Math.round((correction.total_score / maxTotal) * 100);

  const dimensions = [
    {
      label: '语言',
      score: correction.language_score,
      max: dimension_scores?.language?.max_score || (isApplication ? 5 : 7),
      comment: correction.language_comment,
    },
    {
      label: '内容',
      score: correction.content_score,
      max: dimension_scores?.content?.max_score || (isApplication ? 6 : 9),
      comment: correction.content_comment,
    },
    {
      label: '连贯',
      score: correction.coherence_score,
      max: dimension_scores?.coherence?.max_score || (isApplication ? 4 : 5),
      comment: correction.coherence_comment,
    },
    ...(isApplication
      ? []
      : [{
          label: '创意',
          score: correction.creativity_score,
          max: dimension_scores?.creativity?.max_score || 4,
          comment: correction.creativity_comment,
        }]),
  ];

  return (
    <div className="h5-page h5-result-page">
      <section className="h5-score-hero">
        <span>{topic?.title || '批改报告'}</span>
        <div className="h5-score-value">
          <strong>{correction.total_score}</strong>
          <em>/{maxTotal}</em>
        </div>
        <Progress percent={percent} showInfo={false} strokeColor="#53b99a" trailColor="rgba(83,185,154,0.16)" />
        <p>{essay.student?.name || '-'} · {essay.class?.name || '-'}</p>
      </section>

      <section className="h5-panel">
        <div className="h5-section-title">
          <h2>维度评分</h2>
        </div>
        <div className="h5-dimension-list">
          {dimensions.map((item) => (
            <div key={item.label} className="h5-dimension-card">
              <div>
                <strong>{item.label}</strong>
                <span>{item.comment || '暂无评价'}</span>
              </div>
              <Tag color="blue">{item.score.toFixed(1)}/{item.max}</Tag>
            </div>
          ))}
        </div>
      </section>

      <section className="h5-panel">
        <div className="h5-section-title">
          <h2>教师评语</h2>
        </div>
        <div className="h5-comments">
          {generateComments(correction, essay, dimension_scores)}
        </div>
      </section>

      <section className="h5-panel">
        <div className="h5-section-title">
          <h2>错误分析</h2>
          <Tag>{errors.length} 处</Tag>
        </div>
        {errors.length === 0 ? (
          <Alert type="success" showIcon message="没有发现明显错误" />
        ) : (
          <div className="h5-error-list">
            {errors.slice(0, 20).map((error) => (
              <div key={error.id} className="h5-error-card">
                <Tag>{error.error_type}</Tag>
                <p>
                  <del>{error.error_text}</del>
                  <span> → </span>
                  <strong>{error.suggestion}</strong>
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      <Button block type="primary" onClick={() => navigate('/h5/history')}>
        返回记录
      </Button>
    </div>
  );
};

export default H5ResultPage;
